import React, { useCallback, useContext, useEffect, useState } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { PageData } from '@/api/Pagination/Types'
import { SortDescriptor } from '@progress/kendo-data-query'
import { FieldSettings } from '@progress/kendo-react-data-tools'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { TransactionDto, TransactionFilterKey } from 'api/Transaction/Types/Transaction'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import {
  SelectWalletFilter,
  SelectWalletModalOpenEvent,
} from 'components/Filter/WalletFields/SelectWalletFilter'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  setGridFilters,
} from 'store/grid'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import {
  fetchTransactionStatusAction,
  fetchTransactionTypeAction,
} from 'store/transactions/transaction.action'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, BuildQueryProps, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'
import { isTransactionCompletedStatus } from 'utils/transaction/status.index'

import SelectWalletTable, { SelectWalletEvent } from '../Records/Wallets/SelectWalletTable'
import { normalizeTransactions, normalizeTransactionsSortQuery } from './normalizer'
import { GridColumn } from './transactionIBTransactionsColumns'
import { TransactionReverseIBCommissionPage } from './TransactionReverse/TransactionReverseIBCommissionPage'

const useMapStateToProps = () => ({
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  transactionStatus: useAppSelector((state) => state.transactions.status),
  transactionTypes: useAppSelector((state) => state.transactions.types),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const walletsApiClient = useApiClient(WalletsApiClient)

  return {
    fetchTransactionStatus: async () => dispatch(fetchTransactionStatusAction(walletsApiClient)),
    fetchTransactionTypes: async () => dispatch(fetchTransactionTypeAction(walletsApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

interface TransactionIBTransactionsProps {
  filterType: FilterType
  columns: GridColumn[]
  filterFields: () => FieldSettings[]
  query: BuildQueryProps
}

const TransactionIBTransactionsOverview: React.FC<TransactionIBTransactionsProps> = (props) => {
  const { filterType, columns, filterFields, query } = props
  const [auth] = useContext(AuthSessionContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const walletsApiClient = useApiClient(WalletsApiClient)

  const permissions = auth?.permissions || []
  const [transactions, setTransactions] = useState<TransactionDto[]>([])

  const [isWalletModalOpen, setWalletModalOpen] = useState<boolean>(false)
  const [walletModalFilterId, setWalletModalFilterId] = useState<string>()

  const [reverseTransactionModal, setReverseTransactionModalOpen] = useState<{
    visible: boolean
    data?: TransactionDto
  }>({ visible: false })

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { transactionStatus, transactionTypes, tickmillCompanies, gridFilterByFilterIdSelector } =
    useMapStateToProps()
  const { fetchTransactionStatus, fetchTransactionTypes, fetchTickmillCompanies } =
    useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<TransactionDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await walletsApiClient.getIBCommissionsTransactions({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(transactionStatus)) {
            await fetchTransactionStatus()
          }
          if (isEmpty(transactionTypes)) {
            await fetchTransactionTypes()
          }
          if (isEmpty(tickmillCompanies)) {
            await fetchTickmillCompanies()
          }

          if (response?.items) {
            setTransactions(normalizeTransactions(response.items))
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<TransactionDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const gridFilter = filter ?? gridState.filter

      return (
        buildQuery({
          ...gridFilter,
          logic: gridFilter.logic,
          defaultFilters: query.defaultFilters,
        }) ?? null
      )
    },
    [gridState.filter] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeTransactionsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }) => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await walletsApiClient.getIBCommissionsTransactions({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setTransactions(normalizeTransactions(response.items))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await walletsApiClient.getIBCommissionsTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getIBCommissionsTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getIBCommissionsTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await walletsApiClient.getIBCommissionsTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  const onReverseTransaction = async () => {
    setReverseTransactionModalOpen({ visible: false, data: undefined })
    await filterFetch()
  }

  const handleSelectedWallets = (event: SelectWalletEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleWalletModalOpen = (event: SelectWalletModalOpenEvent) => {
    setWalletModalOpen(true)
    setWalletModalFilterId(event.filterId)
  }

  const handleWalletModalClose = () => {
    setWalletModalOpen(false)
    setWalletModalFilterId(undefined)
  }

  const handleReverseTransaction = (transaction: TransactionDto) => {
    setReverseTransactionModalOpen({ visible: true, data: transaction })
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      {reverseTransactionModal.visible && reverseTransactionModal.data && (
        <Modal
          width={600}
          padding='3rem'
          visible={reverseTransactionModal.visible}
          onClose={() => setReverseTransactionModalOpen({ visible: false, data: undefined })}
        >
          <TransactionReverseIBCommissionPage
            transaction={reverseTransactionModal.data}
            onClose={onReverseTransaction}
          />
        </Modal>
      )}
      {isWalletModalOpen && (
        <Modal width={900} visible={isWalletModalOpen} onClose={handleWalletModalClose}>
          <SelectWalletTable
            filterId={walletModalFilterId}
            onSelect={handleSelectedWallets}
            onClose={handleWalletModalClose}
          />
        </Modal>
      )}
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={filterType}
        filterFields={[
          {
            name: TransactionFilterKey.walletTransactionWallet,
            label: 'IB Wallet',
            filter: (walletFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectWalletFilter {...walletFilter} onWalletModalOpen={handleWalletModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          ...filterFields(),
        ]}
        getQuery={getQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport
          data={transactions}
          hasPermissions={hasPermission(permissions, PermissionsEnum.CanExportTransactionsModule)}
        >
          <Grid
            {...gridState}
            resizable
            data={transactions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
            <Column
              width={50}
              title=''
              cell={(gridCellProps: GridCellProps) => {
                if (isTransactionCompletedStatus(gridCellProps.dataItem.state.id)) {
                  return (
                    <td className={generateClassNameForReverseType(gridCellProps.dataItem)}>
                      <MoreButton
                        items={[
                          {
                            name: 'Reverse',
                            disabled: !hasPermission(
                              permissions,
                              PermissionsEnum.CanReverseCustomUserWalletTransaction
                            ),
                            onClick: () => handleReverseTransaction(gridCellProps.dataItem),
                          },
                        ]}
                      />
                    </td>
                  )
                }

                return <td className={generateClassNameForReverseType(gridCellProps.dataItem)} />
              }}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}

export default TransactionIBTransactionsOverview
